/**
 * 本文件包含三个组件：
 * 1. QuestionList - 题目列表组件（含待测/已通过分组折叠面板）
 * 2. QuestionRow - 单个题目行组件
 * 3. QuestionRadioGroup - 单题通过/未通过选择组件
 */
import React, { useCallback, useMemo } from 'react';
import { Collapse, Radio, message } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { A11Y_ROLES } from '@/utils/a11y-attrs';
import { RoadTestItemDetail } from '@/fetch/bussiness/roadtest/types';
import { LocalAnswers, getQuestionResult, setQuestionResult } from '../utils';

export interface QuestionListProps {
  item: RoadTestItemDetail;
  localAnswers: LocalAnswers;
  onChange: (next: LocalAnswers) => void;
}

type RoadTestQuestion = NonNullable<RoadTestItemDetail['questionList']>[number];

/**
 * 题目列表组件
 *
 * @param {*} {
 *   item,
 *   localAnswers,
 *   onChange,
 * }
 * @return {*}
 */
const QuestionList: React.FC<QuestionListProps> = ({
  item,
  localAnswers,
  onChange,
}) => {
  /**
   * 将题目列表分为「待测题目」和「已通过题目」两组
   * 用于渲染待测题目列表和已通过题目折叠面板
   */
  const { pendingQuestions, passedQuestions } = useMemo(() => {
    const pending: RoadTestItemDetail['questionList'] = [];
    const passed: RoadTestItemDetail['questionList'] = [];
    (item.questionList || []).forEach((q) => {
      const result = getQuestionResult(localAnswers, item.itemNo, q.questionNo);
      if (result === true) {
        passed.push(q);
      } else {
        pending.push(q);
      }
    });
    return { pendingQuestions: pending, passedQuestions: passed };
  }, [item, localAnswers]);

  /**
   * 点击题目结果的回调
   * 透传给子子组件
   * @param questionNo 题目编号
   * @param value 题目结果（true: 通过，false: 不通过）
   */
  const handleChange = useCallback(
    (questionNo: string, value: boolean) => {
      if (value === true) {
        message.info('已通过题目已收起，可在「已通过测试项」中查看');
      }

      // 更新本地答案数据，结构化数据
      const next = setQuestionResult(
        localAnswers,
        item.itemNo,
        questionNo,
        value
      );
      // 调用 onChange 回调
      onChange(next);
    },
    [localAnswers, item.itemNo, onChange]
  );

  /**
   * 已通过题目折叠面板的配置项
   */
  const passedPanelItems = useMemo(
    () => [
      {
        key: 'passed',
        label: `已通过测试项（${passedQuestions.length}）`,
        children: (
          <div className="passed-question-group">
            {passedQuestions.map((q) => (
              <QuestionRow
                key={q.questionNo}
                itemNo={item.itemNo}
                question={q}
                localAnswers={localAnswers}
                onChange={handleChange}
              />
            ))}
          </div>
        ),
      },
    ],
    [passedQuestions, item.itemNo, localAnswers, handleChange]
  );

  return (
    <div
      className="question-list"
      data-testid="question-list"
      {...A11Y_ROLES.form(`题目列表 - ${item.itemName}`)}
    >
      <div className="pending-question-group">
        {pendingQuestions.map((q) => (
          <QuestionRow
            key={q.questionNo}
            itemNo={item.itemNo}
            question={q}
            localAnswers={localAnswers}
            onChange={handleChange}
          />
        ))}
      </div>

      {passedQuestions.length > 0 && (
        <Collapse
          key={`collapse-${item.itemNo}`}
          bordered={false}
          defaultActiveKey={[]}
          items={passedPanelItems}
          className="passed-collapse"
          data-testid={`passed-collapse-${item.itemNo}`}
        />
      )}
    </div>
  );
};

interface QuestionRowProps {
  itemNo: string;
  question: RoadTestQuestion;
  localAnswers: LocalAnswers;
  onChange: (questionNo: string, value: boolean) => void;
}

/**
 * 单个题目行组件
 */
const QuestionRow: React.FC<QuestionRowProps> = React.memo(
  ({ itemNo, question, localAnswers, onChange }) => {
    /**
     * 获取题目当前结果（优先从本地缓存取）
     */
    const result = getQuestionResult(localAnswers, itemNo, question.questionNo);

    return (
      <div
        className="question-row"
        data-testid={`question-row-${question.questionNo}`}
      >
        <div
          className="question-title"
          data-testid={`question-title-${question.questionNo}`}
        >
          {question.questionName}
        </div>
        <div className="question-result">
          <span className="result-label">测试结果</span>
          <QuestionRadioGroup
            questionNo={question.questionNo}
            result={result}
            passOptName={question.passOptName}
            failOptName={question.failOptName}
            onChange={onChange}
          />
        </div>
      </div>
    );
  }
);
QuestionRow.displayName = 'QuestionRow';

interface QuestionRadioGroupProps {
  questionNo: string;
  result: boolean | null;
  passOptName: string;
  failOptName: string;
  onChange: (questionNo: string, value: boolean) => void;
}

/**
 * 题目结果单选按钮组组件
 */
const QuestionRadioGroup: React.FC<QuestionRadioGroupProps> = React.memo(
  ({ questionNo, result, passOptName, failOptName, onChange }) => {
    /**
     * 点击题目结果的回调
     */
    const handleChange = useCallback(
      (e: RadioChangeEvent) => {
        onChange(questionNo, e.target.value as boolean);
      },
      [questionNo, onChange]
    );

    return (
      <Radio.Group
        name={`question-${questionNo}`}
        value={result}
        onChange={handleChange}
        data-testid={`question-options-${questionNo}`}
        {...A11Y_ROLES.radio(`题目 ${questionNo} 结果`)}
      >
        <Radio
          value={true}
          {...A11Y_ROLES.radio(passOptName || '通过', result === true)}
        >
          {passOptName || '通过'}
        </Radio>
        <Radio
          value={false}
          {...A11Y_ROLES.radio(failOptName || '不通过', result === false)}
        >
          {failOptName || '不通过'}
        </Radio>
      </Radio.Group>
    );
  }
);
QuestionRadioGroup.displayName = 'QuestionRadioGroup';

export default React.memo(QuestionList);
